import type { ContractResponsibilities, ContractReviewSummary } from "@closer/shared";
import {
  CONTRACT_REVIEW_STATUS_NAMES,
  CONTRACT_REVIEW_TASK_STATUS_NAMES,
  responsibilityTitle,
} from "@closer/shared";
import {
  Badge,
  Button,
  Card,
  Checkbox,
  FormField,
  Input,
  Modal,
  Select,
  Skeleton,
  Textarea,
  useToast,
} from "@closer/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";

import { StateView } from "../../components/StateView";
import { TaskReassignDialog } from "../../components/TaskReassignDialog";
import { UyeListesiDurumu } from "../../components/UyeListesiDurumu";
import { WorkflowCancelDialog } from "../../components/WorkflowCancelDialog";
import { api } from "../../lib/apiClient";
import { useAuth } from "../../lib/authContext";
import { sozlesmeAdaylariSorgusu, sozlesmeSurumSorgusu } from "../../lib/sharedQueries";
import { tenantKey } from "../../lib/tenantQuery";
import { resolveErrorState } from "../../lib/uiState";
import { useIdempotencyKey } from "../../lib/useIdempotencyKey";

/**
 * İNCELEME AKIŞI (CLOS-155 `[148]` · CLOS-157 `[150]`).
 *
 * ### ⚠ İnceleme DEĞİŞMEZ VERSİYONA çakılı
 *
 * Akış başlatılırken sürüm açıkça seçiliyor: inceleyicinin okuduğu metin sonradan
 * değişemiyor. "Güncel metni incele" diye bir seçenek olsaydı, karar hangi metne
 * verildiği belirsiz kalırdı.
 *
 * ### ⚠ Karar YALNIZ kendi görevinde
 *
 * Karar düğmeleri kişinin kendi satırında ve yalnız görev beklerken beliriyor. Başkasının
 * görevini sonuçlandırmak sunucuda 403; ekran onu teklif etmiyor.
 *
 * ### ⚠ "Değişiklik istendi" bir RET DEĞİL
 *
 * İki karar ayrı: tamamlandı ve değişiklik istendi. İkisini tek düğmede toplasaydık,
 * "okudum, uygun" ile "böyle olmaz, düzeltin" aynı kayda düşerdi.
 */
export function ContractReviewSection({
  contractId,
  arsivde = false,
  kayitYazma = true,
}: {
  contractId: string;
  /** Görüntüleyenin bu kayıttaki yazma erişimi (CLOS-888). */
  kayitYazma?: boolean;
  /**
   * ⚠ Arşivdeki sözleşmede AKIŞ BAŞLATILMIYOR (kusur avı, 26 Ağustos). Sunucu dördünü de
   * reddediyor ("Arşivdeki sözleşme ... "); düğmeyi çizmek, kullanıcıyı formu doldurup
   * 409 yemeye göndermekti. Yürüyen akışın komutları KALIYOR: arşivleme onları bitirmiyor.
   */
  arsivde?: boolean;
}) {
  const { activeTenantId, can, user } = useAuth();
  const tenantId = activeTenantId!;
  const queryClient = useQueryClient();
  const toast = useToast();

  const [baslatAcik, setBaslatAcik] = useState(false);
  const [kararHedef, setKararHedef] = useState<{ reviewId: string; taskId: string } | null>(null);

  const anahtar = tenantKey(tenantId, "contract", contractId, "incelemeler");
  const akislar = useQuery({
    queryKey: anahtar,
    queryFn: async () =>
      (await api<ContractReviewSummary[]>(`/api/contracts/${contractId}/reviews`, { tenantId }))
        .data,
  });

  /**
   * ⚠ BEKLEYEN İŞLER de tazeleniyor (kusur avı, 26 Ağustos). Bu bölümdeki her karar
   * sunucuda bekleyen görev satırını kapatıyor ya da açıyor; yalnız kendi anahtarını
   * tazeleseydik üst bardaki rozet ve Ana Sayfa bölümü eski sayıda kalır, kullanıcı biraz
   * önce bitirdiği işi hâlâ "bekliyor" diye görürdü. Üç tüketici de burada: panel rozeti
   * (CLOS-156 `[149]`), Ana Sayfa bölümü ve tam liste sayfası.
   */
  const yenile = async () => {
    await queryClient.invalidateQueries({ queryKey: anahtar });
    await queryClient.invalidateQueries({ queryKey: tenantKey(tenantId, "pending-tasks") });
    await queryClient.invalidateQueries({ queryKey: tenantKey(tenantId, "home") });
    await queryClient.invalidateQueries({ queryKey: ["pending-tasks"] });
  };

  /** Onay bekleyen iptal hedefi; `null` iken diyalog kapalı (CLOS-968). */
  const [iptalHedefi, setIptalHedefi] = useState<string | null>(null);

  /** Devredilecek görev; `null` iken yeniden atama diyaloğu kapalı (CLOS-968). */
  const [devirHedef, setDevirHedef] = useState<{
    reviewId: string;
    taskId: string;
    reviewerUserId: string;
  } | null>(null);

  /** İptal edilecek TEKİL inceleme görevi; `null` iken diyalog kapalı (CLOS-1117 · yetim uç C). */
  const [gorevIptalHedefi, setGorevIptalHedefi] = useState<{
    reviewId: string;
    taskId: string;
  } | null>(null);

  /**
   * TEKİL GÖREV İPTALİ (CLOS-1117 · yetim uç C).
   *
   * ⚠ Uç sunucuda vardı (CLOS-923), ekranı yoktu: izne çıkan inceleyicinin görevi yalnız
   * DEVREDİLEBİLİYOR, kapatılamıyordu. Akış iptalinden farkı: diğer inceleyiciler
   * etkilenmiyor; son açık görevse akışın toplam durumunu SUNUCU türetiyor. Ret cümlesi
   * (kapanmış akış, sonuçlanmış görev) sunucudan geldiği gibi gösteriliyor.
   */
  /**
   * ⚠ HER NİYET KENDİ ANAHTARINI TAŞIR (BUG-004). Anahtar tıklamaya değil NİYETE bağlı:
   * çift tıklama aynı anahtarı gönderir ve sunucu ikincisini replay sayar. Görev iptali
   * ile imzacı/inceleyici devri AYRI niyetler; tek anahtarı paylaşsalardı biri diğerinin
   * yanıtına sessizce düşerdi.
   */
  const idempotencyIptal = useIdempotencyKey();
  const idempotencyDevir = useIdempotencyKey();

  const gorevIptal = useMutation({
    mutationFn: async (girdi: { reviewId: string; taskId: string; reason?: string }) =>
      api(`/api/contracts/${contractId}/reviews/${girdi.reviewId}/tasks/${girdi.taskId}/cancel`, {
        method: "POST",
        tenantId,
        /** ⚠ Uç `idempotency()` zinciriyle korunuyor: aynı iptal iki kez yazılmasın. */
        headers: { "idempotency-key": idempotencyIptal.uret() },
        body: JSON.stringify(girdi.reason ? { reason: girdi.reason } : {}),
      }),
    onSuccess: async () => {
      /** ⚠ Başarıdan sonra yeni niyet yeni anahtar alıyor (BUG-004). */
      idempotencyIptal.yenile();
      setGorevIptalHedefi(null);
      await yenile();
      toast.success("İnceleme görevi iptal edildi");
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : "İnceleme görevi iptal edilemedi"),
  });

  const iptal = useMutation({
    mutationFn: async (reviewId: string) =>
      api(`/api/contracts/${contractId}/reviews/${reviewId}/cancel`, { method: "POST", tenantId }),
    onSuccess: async () => {
      setIptalHedefi(null);
      await yenile();
      toast.success("İnceleme akışı iptal edildi");
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : "İnceleme iptal edilemedi"),
  });

  if (!can("contract.review")) return null;

  const acik = (akislar.data ?? []).find((a) => a.status === "open");

  return (
    <Card>
      <div className="mb-3 flex items-center gap-2">
        <h2 className="mr-auto text-base font-semibold text-fg-heading">İnceleme</h2>
        {!acik && !arsivde && kayitYazma ? (
          <Button type="button" size="sm" variant="secondary" onClick={() => setBaslatAcik(true)}>
            İnceleme başlat
          </Button>
        ) : null}
      </div>

      {akislar.isPending ? (
        <Skeleton lines={3} />
      ) : akislar.error ? (
        <StateView
          state={resolveErrorState(akislar.error, {
            idempotent: true,
            overrides: {
              error: { title: "İnceleme yüklenemedi", description: "Bölüm şu an getirilemedi." },
            },
          })}
          onRetry={() => void akislar.refetch()}
        />
      ) : (akislar.data ?? []).length === 0 ? (
        <p className="text-sm text-fg-muted">Bu sözleşme için henüz inceleme başlatılmadı.</p>
      ) : (
        <div className="flex flex-col gap-3">
          {(akislar.data ?? []).map((akis) => (
            <div
              key={akis.id}
              className="flex flex-col gap-2 rounded-md border border-border-default p-3"
            >
              <div className="flex flex-wrap items-center gap-2">
                <Badge tone={akis.status === "open" ? "warning" : "neutral"}>
                  {CONTRACT_REVIEW_STATUS_NAMES[akis.status]}
                </Badge>
                <span className="text-xs text-fg-soft">
                  Sürüm {akis.versionNumber} · {akis.startedByName}
                  {akis.dueDate
                    ? ` · Son tarih ${new Date(akis.dueDate).toLocaleDateString("tr-TR")}`
                    : ""}
                </span>
                {akis.status === "open" ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => setIptalHedefi(akis.id)}
                  >
                    İptal et
                  </Button>
                ) : null}
              </div>

              <ul className="flex flex-col gap-1">
                {akis.tasks.map((gorev) => (
                  <li
                    key={gorev.id}
                    className="flex flex-wrap items-center justify-between gap-2 border-b border-border-default py-1.5 last:border-0"
                  >
                    <span className="flex min-w-0 flex-col">
                      <span className="truncate text-sm text-fg">{gorev.reviewerName}</span>
                      {gorev.decisionNote ? (
                        <span className="truncate text-xs text-fg-soft">{gorev.decisionNote}</span>
                      ) : null}
                    </span>
                    <span className="flex items-center gap-2">
                      <Badge tone={gorev.status === "completed" ? "success" : "neutral"}>
                        {CONTRACT_REVIEW_TASK_STATUS_NAMES[gorev.status]}
                      </Badge>
                      {/* ⚠ Karar YALNIZ kendi bekleyen görevinde. */}
                      {akis.status === "open" &&
                      gorev.status === "pending" &&
                      gorev.reviewerUserId === user?.id ? (
                        <Button
                          type="button"
                          size="sm"
                          onClick={() => setKararHedef({ reviewId: akis.id, taskId: gorev.id })}
                        >
                          Karar ver
                        </Button>
                      ) : null}
                      {/*
                       * ⚠ Devretme BAŞKASININ görevi için de görünür — anlamı bu (CLOS-968):
                       * izne çıkan inceleyicinin işini akışı yürüten kişi devreder. Kendi
                       * görevi için de duruyor; sunucu aynı uçta iki kapı birden uyguluyor
                       * (`contract.review` yetkisi ve kayıt YAZMA erişimi), yani düğmeyi
                       * görene istek gönderme hakkı da verilmiş oluyor.
                       */}
                      {akis.status === "open" && gorev.status === "pending" ? (
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={() =>
                            setDevirHedef({
                              reviewId: akis.id,
                              taskId: gorev.id,
                              reviewerUserId: gorev.reviewerUserId,
                            })
                          }
                        >
                          Devret
                        </Button>
                      ) : null}
                      {/*
                       * ⚠ Görev iptali "Devret" ile AYNI kapıdan (CLOS-1117 · yetim uç C):
                       * açık akış + bekleyen görev. Akış seviyesindeki "İptal et" bütün
                       * akışı kapatıyor; bu yalnız BU satırın görevini.
                       */}
                      {akis.status === "open" && gorev.status === "pending" ? (
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          data-testid="inceleme-gorev-iptal-ac"
                          onClick={() =>
                            setGorevIptalHedefi({ reviewId: akis.id, taskId: gorev.id })
                          }
                        >
                          İptal et
                        </Button>
                      ) : null}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}

      <IncelemeBaslatDialog
        open={baslatAcik}
        contractId={contractId}
        onClose={() => setBaslatAcik(false)}
        onDone={async () => {
          setBaslatAcik(false);
          await yenile();
        }}
      />
      <IncelemeKararDialog
        open={kararHedef !== null}
        contractId={contractId}
        hedef={kararHedef}
        onClose={() => setKararHedef(null)}
        onDone={async () => {
          setKararHedef(null);
          await yenile();
        }}
      />

      <WorkflowCancelDialog
        open={iptalHedefi !== null}
        onClose={() => setIptalHedefi(null)}
        title="İnceleme akışını iptal et"
        description="Açık inceleme görevleri kapanır ve inceleyicilerin sorumluluğu düşer. Yeniden incelemek için akışı baştan başlatmanız gerekir."
        pending={iptal.isPending}
        onConfirm={() => iptalHedefi && iptal.mutate(iptalHedefi)}
      />

      <IncelemeGoreviIptalDialog
        open={gorevIptalHedefi !== null}
        pending={gorevIptal.isPending}
        onClose={() => setGorevIptalHedefi(null)}
        onConfirm={(reason) =>
          gorevIptalHedefi &&
          gorevIptal.mutate({ ...gorevIptalHedefi, ...(reason ? { reason } : {}) })
        }
      />

      {/*
       * ⚠ Gerekçe İNCELEMEDE isteğe bağlı (`config/workflowCancelReassign.json`: review →
       * `requireReasonForReassign: false`). Onay ve imzada zorunlu, çünkü orada devir karar
       * hiyerarşisini değiştiriyor; incelemede görüş toplama sırası değişmiyor.
       */}
      <TaskReassignDialog
        open={devirHedef !== null}
        onClose={() => setDevirHedef(null)}
        tenantId={tenantId}
        contractId={contractId}
        title="İnceleme görevini devret"
        assigneeLabel="Yeni inceleyici"
        currentAssigneeUserId={devirHedef?.reviewerUserId}
        successMessage="İnceleme görevi devredildi"
        fallbackErrorMessage="Görev devredilemedi"
        onDone={async () => {
          /** ⚠ Devir başarılıysa yeni niyet yeni anahtar alıyor (BUG-004). */
          idempotencyDevir.yenile();
          await yenile();
        }}
        reassign={async ({ userId, reason }) =>
          api(
            `/api/contracts/${contractId}/reviews/${devirHedef!.reviewId}/tasks/${devirHedef!.taskId}/reassign`,
            {
              method: "POST",
              tenantId,
              headers: { "idempotency-key": idempotencyDevir.uret() },
              body: JSON.stringify({ newReviewerUserId: userId, ...(reason ? { reason } : {}) }),
            },
          )
        }
      />
    </Card>
  );
}

/** Sunucudaki `reasonMinLength` (config/workflowCancelReassign.json). */
const IPTAL_GEREKCE_ASGARI = 10;

/**
 * TEKİL İNCELEME GÖREVİ İPTAL DİYALOĞU (CLOS-1117 · yetim uç C).
 *
 * ### ⚠ Neden ortak `WorkflowCancelDialog` DEĞİL
 *
 * O bileşen AKIŞ seviyesindeki gövdesiz `.../cancel` uçları için yazıldı ve gerekçe alanı
 * BİLEREK yok. Görev seviyesindeki uç gövde alıyor (`cancelContractReviewTaskSchema`);
 * gerekçe incelemede İSTEĞE BAĞLI (`requireReasonForCancel: false`) ama girildiyse sunucu
 * `reasonMinLength`'i (10) uyguluyor. Ekran o sınırı kullanıcıyı boşuna reddettirmemek
 * için önden gösteriyor; asıl kapı `applyReasonPolicy`.
 *
 * ⚠ Gerekçe boşsa gövdeye `reason` HİÇ girmiyor: "gerekçe yok" ile "gerekçe boş metin"
 * audit'te iki farklı şey olurdu.
 */
function IncelemeGoreviIptalDialog({
  open,
  pending,
  onClose,
  onConfirm,
}: {
  open: boolean;
  pending: boolean;
  onClose: () => void;
  onConfirm: (reason: string | undefined) => void;
}) {
  const [gerekce, setGerekce] = useState("");

  // Her açılış temiz başlar: önceki denemenin gerekçesi başka bir göreve taşınmaz.
  useEffect(() => {
    if (open) setGerekce("");
  }, [open]);

  const kirpilmis = gerekce.trim();
  const eksik = kirpilmis.length > 0 && kirpilmis.length < IPTAL_GEREKCE_ASGARI;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="İnceleme görevini iptal et"
      size="sm"
      footer={
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            Vazgeç
          </Button>
          <Button
            type="button"
            variant="danger"
            loading={pending}
            disabled={eksik || pending}
            data-testid="inceleme-gorev-iptal-onayla"
            onClick={() => onConfirm(kirpilmis || undefined)}
          >
            İptal et
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-3">
        <p className="text-sm text-fg-muted">
          Yalnız bu inceleyicinin görevi kapanır; diğer inceleyiciler etkilenmez. Açık görev
          kalmazsa akışın sonucu kalan kararlardan belirlenir.
        </p>
        <FormField
          label="Gerekçe (isteğe bağlı)"
          error={eksik ? `Gerekçe en az ${IPTAL_GEREKCE_ASGARI} karakter olmalıdır` : undefined}
        >
          <Textarea
            id="inceleme-gorev-iptal-gerekce"
            value={gerekce}
            onChange={(olay) => setGerekce(olay.target.value)}
            rows={3}
            placeholder="Görevin neden iptal edildiğini yazın."
          />
        </FormField>
      </div>
    </Modal>
  );
}

/** İnceleme başlatma — sürüm, inceleyiciler ve zorunlu son tarih (ADR-0065). */
function IncelemeBaslatDialog({
  open,
  contractId,
  onClose,
  onDone,
}: {
  open: boolean;
  contractId: string;
  onClose: () => void;
  onDone: () => Promise<void> | void;
}) {
  const { activeTenantId } = useAuth();
  const tenantId = activeTenantId!;
  const toast = useToast();

  const [versionId, setVersionId] = useState("");
  const [secilenler, setSecilenler] = useState<string[]>([]);
  const [dueDate, setDueDate] = useState("");

  const versiyonlar = useQuery({
    enabled: open,
    ...sozlesmeSurumSorgusu(tenantId, contractId),
  });

  /** ⚠ Adaylar `contract.review` yeteneğine göre süzülü (BUG-009/009b kardeşi). */
  const uyeler = useQuery({
    enabled: open,
    ...sozlesmeAdaylariSorgusu(tenantId, contractId, "contract.review"),
  });

  /**
   * ÖNERİ KAYNAĞI (CLOS-1126 · UAT-48): sayfadaki sorumluluk listesiyle AYNI anahtar.
   * Yalnız ön işaretleme; sunucu şeması ve karar yetkisi değişmez — öneri authority üretmez.
   */
  const sorumlular = useQuery({
    enabled: open,
    queryKey: tenantKey(tenantId, "contract", contractId, "sorumlular"),
    queryFn: async () =>
      (
        await api<ContractResponsibilities>(`/api/contracts/${contractId}/responsibilities`, {
          tenantId,
        })
      ).data,
    retry: false,
  });

  useEffect(() => {
    if (!open) return;
    setSecilenler([]);
    setDueDate("");
    oneriUygulandi.current = false;
  }, [open]);
  useEffect(() => {
    if (open) setVersionId(versiyonlar.data?.[0]?.id ?? "");
  }, [open, versiyonlar.data]);
  /**
   * PREFILL (CLOS-1126 · Halit routing): Hukuki Sorumlu → Sözleşme Sorumlusu önceden
   * işaretli; kullanıcı kaldırabilir. Açılış başına bir kez; boş listeye yazar, seçimi ezmez.
   */
  const oneriUygulandi = useRef(false);
  const oneri = (() => {
    const veri = sorumlular.data;
    if (!veri) return null;
    const satir = veri.contributors.find((k) => k.area === "legal") ?? veri.owner;
    if (!satir?.userId) return null;
    const uye = uyeler.data?.candidates.find((u) => u.userId === satir.userId);
    /** ⚠ Aday listesi zaten yalnız aktif üyelikleri taşıyor (BUG-002). */
    if (!uye) return null;
    return { userId: satir.userId, ad: uye.name, unvan: responsibilityTitle(satir) };
  })();
  const oneriUserId = oneri?.userId ?? null;
  useEffect(() => {
    if (!open || !oneriUserId || oneriUygulandi.current) return;
    oneriUygulandi.current = true;
    setSecilenler((onceki) => (onceki.length === 0 ? [oneriUserId] : onceki));
  }, [open, oneriUserId]);

  const baslat = useMutation({
    mutationFn: async () =>
      api(`/api/contracts/${contractId}/reviews`, {
        method: "POST",
        tenantId,
        body: JSON.stringify({
          versionId,
          reviewerUserIds: secilenler,
          /** ⚠ Son tarih ZORUNLU (ADR-0065); düğme boş tarihte kapalı olduğu için HER ZAMAN gider. */
          dueDate: new Date(`${dueDate}T23:59:59`).toISOString(),
        }),
      }),
    onSuccess: async () => {
      toast.success("İnceleme başlatıldı");
      await onDone();
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : "İnceleme başlatılamadı"),
  });

  const aktifUyeler = uyeler.data?.candidates ?? [];

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="İnceleme başlat"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Vazgeç
          </Button>
          <Button
            onClick={() => baslat.mutate()}
            loading={baslat.isPending}
            disabled={versionId === "" || secilenler.length === 0 || dueDate === ""}
          >
            Başlat
          </Button>
        </>
      }
    >
      {versiyonlar.isPending || uyeler.isPending ? (
        <div role="status" aria-label="Seçenekler yükleniyor">
          <Skeleton lines={3} />
        </div>
      ) : (versiyonlar.data ?? []).length === 0 ? (
        <p className="text-sm text-fg-muted">
          İnceleme için önce bir sürüm oluşturmalısınız; inceleme değişmez bir metne bağlanır.
        </p>
      ) : (
        <div className="flex flex-col gap-3">
          <FormField label="İncelenecek sürüm">
            <Select
              id="inceleme-versiyon"
              value={versionId}
              onChange={(olay) => setVersionId(olay.target.value)}
            >
              {(versiyonlar.data ?? []).map((v) => (
                <option key={v.id} value={v.id}>
                  Sürüm {v.versionNumber} · {new Date(v.createdAt).toLocaleDateString("tr-TR")}
                </option>
              ))}
            </Select>
          </FormField>

          <fieldset className="flex flex-col gap-1">
            <legend className="text-sm font-medium text-fg-strong">İnceleyiciler</legend>
            {aktifUyeler.map((u) => (
              <Checkbox
                key={u.userId}
                label={u.name}
                checked={secilenler.includes(u.userId)}
                onChange={(olay) =>
                  setSecilenler((onceki) =>
                    olay.target.checked
                      ? [...onceki, u.userId]
                      : onceki.filter((x) => x !== u.userId),
                  )
                }
              />
            ))}
            <UyeListesiDurumu hata={uyeler.error} bosMu={aktifUyeler.length === 0} />
            {oneri ? (
              <p className="text-xs text-fg-soft" data-testid="inceleyici-onerisi">
                Öneri: {oneri.unvan} {oneri.ad} önceden işaretlendi; öneri karar yetkisi vermez.
              </p>
            ) : null}
          </fieldset>

          <FormField
            label="Son tarih"
            required
            hint="Süreç başlatılırken son tarih seçilmelidir (ADR-0065)."
          >
            <Input
              id="inceleme-son-tarih"
              type="date"
              required
              value={dueDate}
              onChange={(olay) => setDueDate(olay.target.value)}
            />
          </FormField>
        </div>
      )}
    </Modal>
  );
}

/**
 * İnceleme kararı.
 *
 * ⚠ "Değişiklik istendi" gerekçe istiyor: sunucu boş gerekçeyi reddediyor ve ekran da
 * kararı gerekçesiz göndermiyor — kullanıcıya sunucunun reddini denettirmemek için.
 */
function IncelemeKararDialog({
  open,
  contractId,
  hedef,
  onClose,
  onDone,
}: {
  open: boolean;
  contractId: string;
  hedef: { reviewId: string; taskId: string } | null;
  onClose: () => void;
  onDone: () => Promise<void> | void;
}) {
  const { activeTenantId } = useAuth();
  const tenantId = activeTenantId!;
  const toast = useToast();

  const [decision, setDecision] = useState<"completed" | "changes_requested">("completed");
  const [note, setNote] = useState("");

  useEffect(() => {
    if (!open) return;
    setDecision("completed");
    setNote("");
  }, [open]);

  const karar = useMutation({
    mutationFn: async () => {
      if (!hedef) throw new Error("Görev seçilmedi");
      return api(
        `/api/contracts/${contractId}/reviews/${hedef.reviewId}/tasks/${hedef.taskId}/decide`,
        {
          method: "POST",
          tenantId,
          body: JSON.stringify({ decision, ...(note.trim() ? { note: note.trim() } : {}) }),
        },
      );
    },
    onSuccess: async () => {
      toast.success("İnceleme kararı kaydedildi");
      await onDone();
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : "Karar kaydedilemedi"),
  });

  const gerekceZorunlu = decision === "changes_requested";

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="İnceleme kararı"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Vazgeç
          </Button>
          <Button
            onClick={() => karar.mutate()}
            loading={karar.isPending}
            disabled={gerekceZorunlu && note.trim() === ""}
          >
            Kararı kaydet
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <p className="text-xs text-fg-soft">
          Karar geri alınamaz; düzeltme gerekiyorsa akış iptal edilip yenisi başlatılır.
        </p>
        <FormField label="Karar">
          <Select
            id="inceleme-karar"
            value={decision}
            onChange={(olay) => setDecision(olay.target.value as "completed" | "changes_requested")}
          >
            <option value="completed">İnceledim, uygun</option>
            <option value="changes_requested">Değişiklik istiyorum</option>
          </Select>
        </FormField>
        <FormField label={gerekceZorunlu ? "Gerekçe (zorunlu)" : "Not (opsiyonel)"}>
          <Input
            id="inceleme-not"
            value={note}
            onChange={(olay) => setNote(olay.target.value)}
            placeholder="Örn. 4. madde bedel tanımı netleştirilmeli"
          />
        </FormField>
      </div>
    </Modal>
  );
}
